import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Keyboard navigation for a list of records.
 *
 * Mnemonics bind labelled controls; a table row has no label and 50 rows cannot
 * each hold a letter, so lists need their own affordance. This supplies exactly
 * that — move, open, toggle — and nothing more.
 *
 * Spreads onto existing markup rather than requiring a shared table component,
 * so adopting it on a page is a three-line diff and no styling changes:
 *
 *   const { getListProps, getRowProps } = useListNavigation({ count, onSelect });
 *   <tbody {...getListProps()}>
 *     {rows.map((r, i) => <tr key={r.id} {...getRowProps(i)}>…</tr>)}
 *
 * Uses roving tabindex: only the active row is tabbable, so Tab moves past the
 * list instead of through every row.
 */
export function useListNavigation({ count, onSelect, onToggle, enabled = true } = {}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const rowRefs = useRef([]);
  const hasFocus = useRef(false);

  // keep the cursor in range when the list is filtered or paginated
  useEffect(() => {
    setActiveIndex((current) => (count === 0 ? 0 : Math.min(current, count - 1)));
  }, [count]);

  const focusRow = useCallback((index) => {
    const node = rowRefs.current[index];
    if (!node) return;
    node.focus();
    node.scrollIntoView?.({ block: 'nearest' });
  }, []);

  const move = useCallback(
    (delta) => {
      setActiveIndex((current) => {
        const next = Math.max(0, Math.min(count - 1, current + delta));
        if (hasFocus.current) focusRow(next);
        return next;
      });
    },
    [count, focusRow]
  );

  const jumpTo = useCallback(
    (index) => {
      setActiveIndex(() => {
        if (hasFocus.current) focusRow(index);
        return index;
      });
    },
    [focusRow]
  );

  const onKeyDown = useCallback(
    (event) => {
      if (!enabled || count === 0) return;
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          move(1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          move(-1);
          break;
        case 'Home':
          event.preventDefault();
          jumpTo(0);
          break;
        case 'End':
          event.preventDefault();
          jumpTo(count - 1);
          break;
        case 'Enter':
          event.preventDefault();
          onSelect?.(activeIndex);
          break;
        case ' ':
        case 'Spacebar':
          if (!onToggle) return;
          event.preventDefault();
          onToggle(activeIndex);
          break;
        default:
      }
    },
    [enabled, count, move, jumpTo, onSelect, onToggle, activeIndex]
  );

  const getListProps = useCallback(() => ({ onKeyDown }), [onKeyDown]);

  const getRowProps = useCallback(
    (index) => ({
      ref: (node) => {
        rowRefs.current[index] = node;
      },
      tabIndex: enabled && index === activeIndex ? 0 : -1,
      'aria-selected': index === activeIndex,
      onFocus: () => {
        hasFocus.current = true;
        setActiveIndex(index);
      },
      onBlur: () => {
        hasFocus.current = false;
      },
    }),
    [activeIndex, enabled]
  );

  return { activeIndex, setActiveIndex: jumpTo, getListProps, getRowProps, focusRow };
}

export default useListNavigation;
